import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import Toast from '../../ui/Toast';
import Banner from '../../ui/Banner';
import assistantApi, { getAssistantErrorMessage } from './api/assistantApi';
import AssistantMessage from './AssistantMessage';
import SlashCommandMenu, {
  SLASH_MENU_ID,
  filterCommands,
  getSlashQuery,
  slashOptionId,
} from './SlashCommandMenu';
import './AiAssistantView.css';

// Fallbacks only; the real limits are loaded from GET /assistant/config.
const DEFAULT_LIMITS = { questionChars: 2000, roomNameChars: 120 };

export const REPORT_ASPECTS = [
  {
    id: 'overall',
    label: 'Overall Executive Analysis',
    desc: 'Pipeline health, win rate, campaign reach, attribution, and credit summary.',
    icon: 'fa-chart-line',
  },
  {
    id: 'cost',
    label: 'Cost & Credit Usage',
    desc: 'Credit balance, burn rate, provider breakdown, top users, and runway.',
    icon: 'fa-coins',
  },
  {
    id: 'meetings',
    label: 'Meetings & Scheduling',
    desc: 'Completed sessions, upcoming bookings, recap coverage, and attendee outcomes.',
    icon: 'fa-calendar-check',
  },
  {
    id: 'funnel',
    label: 'Lead Funnel & Pipeline',
    desc: 'Stage-by-stage progression, conversion velocity, deal values, and loss analysis.',
    icon: 'fa-filter',
  },
  {
    id: 'attribution',
    label: 'Channel Attribution',
    desc: 'First-touch, last-touch, and linear model deal and revenue distribution.',
    icon: 'fa-arrows-split-up-and-left',
  },
  {
    id: 'discovery',
    label: 'Discovery History',
    desc: 'Discover search queries, target job titles, company segments, and saved leads.',
    icon: 'fa-magnifying-glass-chart',
  },
  {
    id: 'seo_landing',
    label: 'SEO & Landing',
    desc: 'Inbound lead share, form submission subjects, landing page yields, audit health, and AI visibility.',
    icon: 'fa-globe',
  },
];

export const REPORT_PERIODS = [
  { days: 7, label: 'Last 7 Days' },
  { days: 30, label: 'Last 30 Days' },
  { days: 90, label: 'Last 90 Days' },
];

export const SUGGESTIONS = [
  {
    icon: 'fa-magnifying-glass-chart',
    label: 'Discovery history',
    prompt: 'What have I been searching for in Discover over the last 30 days?',
  },
  {
    icon: 'fa-users-gear',
    label: 'Lead pipeline',
    prompt: 'Which leads did we lose and why?',
  },
  {
    icon: 'fa-calendar-check',
    label: 'Meetings',
    prompt: 'Summarise our meetings from the last 2 weeks and their outcomes.',
  },
  {
    icon: 'fa-bullhorn',
    label: 'Campaigns',
    prompt: 'How are our campaigns doing and which need attention?',
  },
  {
    icon: 'fa-coins',
    label: 'Credit usage',
    prompt: 'Where did our credits go in the last 30 days?',
  },
];

/**
 * @param {boolean} embedded - rendered as a tab inside the Analytics and
 *   Attribution module (the module owns the banner and page padding).
 */
const AiAssistantView = ({ embedded = false }) => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { workspaceId } = useWorkspace();

  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [deletingRoomId, setDeletingRoomId] = useState(null);
  const [renamingRoomId, setRenamingRoomId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [commands, setCommands] = useState([]);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const MAX_LENGTH = limits.questionChars;
  const [slashIndex, setSlashIndex] = useState(0);
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportAspect, setReportAspect] = useState('overall');
  const [reportPeriod, setReportPeriod] = useState(30);
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const menuButtonRef = useRef(null);

  const listEndRef = useRef(null);
  const inputRef = useRef(null);
  // Prevents the room-change effect from wiping optimistic messages when we
  // navigate to a room we just created while sending.
  const skipNextLoadRef = useRef(false);

  const showError = (message) => setToast({ message, type: 'error' });

  const loadRooms = useCallback(async () => {
    setRoomsLoading(true);
    try {
      setRooms(await assistantApi.listRooms(workspaceId));
    } catch (err) {
      showError(getAssistantErrorMessage(err, 'Could not load conversations.'));
    } finally {
      setRoomsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  // Slash-command registry for the "/" tips. Non-blocking: if it fails the
  // assistant still works, the server still understands typed commands.
  useEffect(() => {
    let cancelled = false;
    assistantApi
      .listCommands(workspaceId)
      .then((list) => {
        if (!cancelled) setCommands(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    assistantApi
      .getConfig(workspaceId)
      .then((cfg) => {
        if (!cancelled && cfg?.limits) setLimits({ ...DEFAULT_LIMITS, ...cfg.limits });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    if (!roomId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setMessagesLoading(true);
      try {
        const data = await assistantApi.getMessages(workspaceId, roomId);
        if (!cancelled) setMessages(data.messages || []);
      } catch (err) {
        if (!cancelled) {
          showError(getAssistantErrorMessage(err, 'Could not load this conversation.'));
          if (err?.response?.status === 404) navigate('/ai-assistant', { replace: true });
        }
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roomId, workspaceId, navigate]);

  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const upsertRoom = (room) => {
    if (!room) return;
    setRooms((prev) => [room, ...prev.filter((r) => r.id !== room.id)]);
  };

  const closeHistory = useCallback(() => {
    setHistoryOpen(false);
    setDeletingRoomId(null);
    setRenamingRoomId(null);
    menuButtonRef.current?.focus();
  }, []);

  // Close the history drawer with Escape.
  useEffect(() => {
    if (!historyOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeHistory();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [historyOpen, closeHistory]);

  const openRoom = (id) => {
    navigate(`/ai-assistant/${id}`);
    setHistoryOpen(false);
    inputRef.current?.focus();
  };

  const handleNewChat = async () => {
    setHistoryOpen(false);
    try {
      const room = await assistantApi.createRoom(workspaceId);
      upsertRoom(room);
      navigate(`/ai-assistant/${room.id}`);
      inputRef.current?.focus();
    } catch (err) {
      showError(getAssistantErrorMessage(err, 'Could not start a new conversation.'));
    }
  };

  const handleDelete = async (id) => {
    try {
      await assistantApi.deleteRoom(workspaceId, id);
      setRooms((prev) => prev.filter((r) => r.id !== id));
      if (id === roomId) navigate('/ai-assistant');
    } catch (err) {
      showError(getAssistantErrorMessage(err, 'Could not delete the conversation.'));
    } finally {
      setDeletingRoomId(null);
    }
  };

  const handleRename = async (id) => {
    const name = renameValue.trim();
    setRenamingRoomId(null);
    if (!name) return;
    try {
      const room = await assistantApi.renameRoom(workspaceId, id, name);
      setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, name: room.name } : r)));
    } catch (err) {
      showError(getAssistantErrorMessage(err, 'Could not rename the conversation.'));
    }
  };

  const sendQuestion = async (text) => {
    const question = String(text || '').trim();
    if (!question || isSending) return;
    // Client-side shortcut: start a new conversation.
    if (/^\/new\s*$/i.test(question)) {
      setInput('');
      handleNewChat();
      return;
    }
    if (question.length > MAX_LENGTH) {
      showError(`Questions can be up to ${MAX_LENGTH} characters.`);
      return;
    }

    setIsSending(true);
    setInput('');
    const tempUser = {
      id: `tmp-user-${Date.now()}`,
      role: 'user',
      content: question,
      status: 'completed',
      createdAt: new Date().toISOString(),
    };
    const tempPending = { id: `tmp-ai-${Date.now()}`, role: 'assistant', status: 'pending' };
    setMessages((prev) => [...prev, tempUser, tempPending]);

    let targetRoomId = roomId;
    try {
      if (!targetRoomId) {
        const room = await assistantApi.createRoom(workspaceId);
        targetRoomId = room.id;
        upsertRoom(room);
        skipNextLoadRef.current = true;
        navigate(`/ai-assistant/${room.id}`, { replace: true });
      }
      const result = await assistantApi.sendMessage(workspaceId, targetRoomId, question);
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempUser.id && m.id !== tempPending.id),
        result.userMessage,
        result.assistantMessage,
      ]);
      upsertRoom(result.room);
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== tempPending.id));
      setInput(question);
      showError(getAssistantErrorMessage(err, 'Could not send your question.'));
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuestion(input);
  };

  const handleDownloadReport = async (report) => {
    if (!report?.id || downloadingReportId) return;
    setDownloadingReportId(report.id);
    try {
      await assistantApi.downloadReport(workspaceId, report.id, report.filename);
      setToast({ message: `Downloaded ${report.filename || 'report.pdf'}`, type: 'success' });
    } catch (err) {
      showError(getAssistantErrorMessage(err, 'Could not download the PDF report.'));
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleModalGenerateReport = () => {
    const aspect = reportAspect || 'overall';
    setReportModalOpen(false);
    sendQuestion(`/report ${aspect} ${reportPeriod} days`);
  };

  // ── "/" shortcuts ───────────────────────────────────────────────────────
  const slashQuery = getSlashQuery(input);
  const slashMatches = filterCommands(commands, slashQuery);
  const slashMenuOpen = slashQuery !== null && !slashDismissed && commands.length > 0;

  const applySlashCommand = (cmd) => {
    if (!cmd) return;
    if (cmd.kind === 'client' && cmd.command === 'new') {
      setInput('');
      handleNewChat();
      return;
    }
    if (!cmd.takesArgument) {
      sendQuestion(`/${cmd.command}`);
      return;
    }
    setInput(`/${cmd.command} `);
    setSlashDismissed(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (slashMenuOpen) {
      if (e.key === 'ArrowDown' && slashMatches.length) {
        e.preventDefault();
        setSlashIndex((i) => (i + 1) % slashMatches.length);
        return;
      }
      if (e.key === 'ArrowUp' && slashMatches.length) {
        e.preventDefault();
        setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashDismissed(true);
        return;
      }
      const highlighted = slashMatches[slashIndex];
      if (e.key === 'Tab' && highlighted) {
        e.preventDefault();
        applySlashCommand(highlighted);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing && highlighted) {
        e.preventDefault();
        // Typed the full name already (e.g. "/help") -> run it; otherwise complete it.
        if (slashQuery === highlighted.command && highlighted.kind !== 'client') {
          sendQuestion(input);
        } else {
          applySlashCommand(highlighted);
        }
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      sendQuestion(input);
    }
  };

  const activeRoom = rooms.find((r) => r.id === roomId);
  const showEmptyState = !messagesLoading && messages.length === 0;

  return (
    <div className={`ai-assistant-page ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
        <Banner
          type="page-header"
          title="Analytics and Attribution"
          subtitle="Ask questions about your discovery history, lead pipeline, meetings, campaigns, and credit usage."
        />
      )}

      <div className="ai-assistant-layout">
        {historyOpen && (
          <div className="ai-assistant-drawer-backdrop" onClick={closeHistory} aria-hidden="true" />
        )}
        <aside
          id="ai-assistant-history"
          className={`ai-assistant-rooms ${historyOpen ? 'open' : ''}`}
          aria-label="Chat history"
          aria-hidden={!historyOpen}
          inert={historyOpen ? undefined : ''}
        >
          <div className="ai-assistant-rooms-header">
            <div className="ai-assistant-rooms-title">
              <h2>Chat history</h2>
              <button
                type="button"
                className="ai-assistant-icon-btn"
                onClick={closeHistory}
                aria-label="Close chat history"
                title="Close"
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <button
              type="button"
              className="ai-assistant-new-btn"
              onClick={handleNewChat}
              disabled={isSending}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> New chat
            </button>
          </div>

          {roomsLoading ? (
            <div className="ai-assistant-rooms-empty" role="status">
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Loading...
            </div>
          ) : rooms.length === 0 ? (
            <div className="ai-assistant-rooms-empty">No conversations yet</div>
          ) : (
            <ul className="ai-assistant-room-list">
              {rooms.map((room) => (
                <li
                  key={room.id}
                  className={`ai-assistant-room ${room.id === roomId ? 'active' : ''}`}
                >
                  {renamingRoomId === room.id ? (
                    <input
                      className="ai-assistant-rename-input"
                      aria-label="Conversation name"
                      autoFocus
                      maxLength={limits.roomNameChars}
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleRename(room.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRename(room.id);
                        if (e.key === 'Escape') setRenamingRoomId(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="ai-assistant-room-link"
                      onClick={() => openRoom(room.id)}
                      aria-current={room.id === roomId ? 'page' : undefined}
                      title={room.name}
                    >
                      <i className="fa-solid fa-comment" aria-hidden="true"></i>
                      <span>{room.name}</span>
                    </button>
                  )}

                  {deletingRoomId === room.id ? (
                    <div className="ai-assistant-room-actions confirm">
                      <button
                        type="button"
                        className="ai-assistant-icon-btn danger"
                        onClick={() => handleDelete(room.id)}
                        aria-label={`Confirm delete ${room.name}`}
                        title="Confirm delete"
                      >
                        <i className="fas fa-check" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        className="ai-assistant-icon-btn"
                        onClick={() => setDeletingRoomId(null)}
                        aria-label="Cancel delete"
                        title="Cancel"
                      >
                        <i className="fas fa-times" aria-hidden="true"></i>
                      </button>
                    </div>
                  ) : (
                    renamingRoomId !== room.id && (
                      <div className="ai-assistant-room-actions">
                        <button
                          type="button"
                          className="ai-assistant-icon-btn"
                          onClick={() => {
                            setRenamingRoomId(room.id);
                            setRenameValue(room.name);
                          }}
                          aria-label={`Rename ${room.name}`}
                          title="Rename"
                        >
                          <i className="fas fa-pen" aria-hidden="true"></i>
                        </button>
                        <button
                          type="button"
                          className="ai-assistant-icon-btn"
                          onClick={() => setDeletingRoomId(room.id)}
                          aria-label={`Delete ${room.name}`}
                          title="Delete"
                        >
                          <i className="fas fa-trash-can" aria-hidden="true"></i>
                        </button>
                      </div>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="ai-assistant-main" aria-label="Conversation">
          <header className="ai-assistant-main-header">
            <button
              type="button"
              ref={menuButtonRef}
              className="ai-assistant-menu-btn"
              onClick={() => setHistoryOpen((v) => !v)}
              aria-label={historyOpen ? 'Close chat history' : 'Open chat history'}
              aria-expanded={historyOpen}
              aria-controls="ai-assistant-history"
              title="Chat history"
            >
              <i className="fas fa-bars" aria-hidden="true"></i>
            </button>
            <div className="ai-assistant-main-header-text">
              <h1>{activeRoom?.name || 'Ask about your sales data'}</h1>
              <p title="Answers are generated from records in your workspace that you can access. The assistant is read-only and can make mistakes, so verify important figures.">
                Based on your workspace records · Read-only · Please verify important figures
              </p>
            </div>
            <div className="ai-assistant-main-header-actions">
              <button
                type="button"
                className="ai-assistant-header-report-btn"
                onClick={() => setReportModalOpen(true)}
                disabled={isSending}
                title="Generate analysis report in PDF format"
              >
                <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>
                <span>Generate Report</span>
              </button>
            </div>
          </header>

          <div className="ai-assistant-messages">
            {messagesLoading ? (
              <div className="ai-assistant-center" role="status">
                <i className="fas fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
              </div>
            ) : showEmptyState ? (
              <div className="ai-assistant-empty">
                <div className="ai-assistant-empty-icon" aria-hidden="true">
                  <i className="fa-solid fa-robot"></i>
                </div>
                <h2>How can I help?</h2>
                <p>
                  Pick an analysis or type your own question. Type <code>/</code> for shortcuts, or{' '}
                  <code>/help</code> for a quick guide.
                </p>
                <div className="ai-assistant-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      className="ai-assistant-suggestion"
                      onClick={() => sendQuestion(s.prompt)}
                      disabled={isSending}
                    >
                      <i className={`fas ${s.icon}`} aria-hidden="true"></i>
                      <span className="ai-assistant-suggestion-label">{s.label}</span>
                      <span className="ai-assistant-suggestion-prompt">{s.prompt}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <ul className="ai-assistant-message-list" aria-live="polite">
                {messages.map((m) => (
                  <AssistantMessage
                    key={m.id}
                    message={m}
                    onDownloadReport={handleDownloadReport}
                    downloadingReportId={downloadingReportId}
                  />
                ))}
                <li ref={listEndRef} aria-hidden="true" />
              </ul>
            )}
          </div>

          <form className="ai-assistant-composer" onSubmit={handleSubmit}>
            {slashMenuOpen && (
              <SlashCommandMenu
                commands={slashMatches}
                activeIndex={slashIndex}
                onSelect={applySlashCommand}
                onHover={setSlashIndex}
              />
            )}
            <label htmlFor="ai-assistant-input" className="ai-assistant-sr-only">
              Ask the AI Assistant. Type / for shortcuts.
            </label>
            <textarea
              id="ai-assistant-input"
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setSlashDismissed(false);
                setSlashIndex(0);
              }}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={slashMenuOpen}
              aria-controls={slashMenuOpen ? SLASH_MENU_ID : undefined}
              aria-activedescendant={
                slashMenuOpen && slashMatches[slashIndex]
                  ? slashOptionId(slashMatches[slashIndex].command)
                  : undefined
              }
              placeholder='Ask a question, or type "/" for shortcuts (e.g. /help)'
              rows={1}
              maxLength={MAX_LENGTH}
              disabled={isSending}
            />
            <button
              type="submit"
              className="ai-assistant-send-btn"
              disabled={isSending || !input.trim()}
              aria-label="Send question"
            >
              {isSending ? (
                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
              ) : (
                <i className="fas fa-paper-plane" aria-hidden="true"></i>
              )}
            </button>
          </form>
        </section>
      </div>

      {reportModalOpen && (
        <div
          className="ai-assistant-modal-backdrop"
          onClick={() => setReportModalOpen(false)}
          role="presentation"
        >
          <div
            className="ai-assistant-report-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ai-assistant-modal-header">
              <div className="ai-assistant-modal-title-wrap">
                <div className="ai-assistant-modal-icon" aria-hidden="true">
                  <i className="fa-solid fa-file-pdf"></i>
                </div>
                <div>
                  <h2 id="report-modal-title">Generate Analysis Report</h2>
                  <p>Export executive PDF analysis for your active workspace.</p>
                </div>
              </div>
              <button
                type="button"
                className="ai-assistant-icon-btn"
                onClick={() => setReportModalOpen(false)}
                aria-label="Close report dialog"
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>

            <div className="ai-assistant-modal-body">
              <label className="ai-assistant-modal-label">Choose Analysis Aspect</label>
              <div className="ai-assistant-aspect-grid">
                {REPORT_ASPECTS.map((asp) => (
                  <button
                    key={asp.id}
                    type="button"
                    className={`ai-assistant-aspect-card ${reportAspect === asp.id ? 'selected' : ''}`}
                    onClick={() => setReportAspect(asp.id)}
                  >
                    <div className="ai-assistant-aspect-icon">
                      <i className={`fas ${asp.icon}`} aria-hidden="true"></i>
                    </div>
                    <div className="ai-assistant-aspect-text">
                      <span className="ai-assistant-aspect-name">{asp.label}</span>
                      <span className="ai-assistant-aspect-desc">{asp.desc}</span>
                    </div>
                    <div className="ai-assistant-aspect-check" aria-hidden="true">
                      {reportAspect === asp.id && <i className="fas fa-check-circle"></i>}
                    </div>
                  </button>
                ))}
              </div>

              <label className="ai-assistant-modal-label" style={{ marginTop: 16 }}>
                Timeframe Window
              </label>
              <div className="ai-assistant-period-pills">
                {REPORT_PERIODS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    className={`ai-assistant-period-pill ${reportPeriod === p.days ? 'active' : ''}`}
                    onClick={() => setReportPeriod(p.days)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ai-assistant-modal-footer">
              <button
                type="button"
                className="ai-assistant-btn-secondary"
                onClick={() => setReportModalOpen(false)}
                disabled={isSending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ai-assistant-btn-primary"
                onClick={handleModalGenerateReport}
                disabled={isSending}
              >
                <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>
                <span>Generate PDF Report</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'error' })}
          duration={5000}
        />
      )}
    </div>
  );
};

export default AiAssistantView;
